import axios, { AxiosInstance } from 'axios';
import { getVoiceFallbackClient } from './_shared';
import { fetchOutboundWebhookConfig, saveOutboundWebhookConfig } from './voiceWorkflowsList';
import type { BurstCostEstimation } from './voiceWorkflowsList';

// ── Webhook subscriptions (/api/webhook-endpoints) ─────────────────────
// F8 (2026-08-19): a distinct, real subscription-management resource — NOT
// the per-campaign fetchOutboundWebhookConfig/saveOutboundWebhookConfig above
// (that's a single url+secret per campaign; this is multi-endpoint,
// multi-event, with a delivery log and redrive). Verified against the live
// WebhookEndpointCreate/WebhookEndpointResponse/DeliveryResponse schema.
// `events` has no enum in the schema (plain string[] — "Namespaced event
// types, or ['*'] for everything") so there's no way to offer a verified
// exhaustive checklist; the UI offers quick-add chips only for the namespace
// wildcards actually seen referenced elsewhere in the live spec's text
// (session.*, dial.*, campaign.*, segment.*, plus the confirmed '*'), with
// free text for anything else. No PATCH/DELETE endpoint exists for a single
// webhook endpoint — only create, list, view deliveries, and redrive.

export type WebhookEndpoint = {
  id: string;
  url: string;
  events: string[];
  active: boolean;
  created_at: string;
};

export type WebhookDelivery = {
  id: string;
  event_type: string;
  status: string;
  attempts: number;
  response_code: number | null;
  last_error: string | null;
  delivered_at: string | null;
  created_at: string;
};

export async function createWebhookEndpoint(
  payload: { url: string; events: string[]; secret?: string },
  axiosInstance?: AxiosInstance
): Promise<WebhookEndpoint> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<WebhookEndpoint>('/api/webhook-endpoints', payload);
  return response.data;
}

export async function fetchWebhookEndpoints(axiosInstance?: AxiosInstance): Promise<WebhookEndpoint[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<WebhookEndpoint[]>('/api/webhook-endpoints');
  return response.data;
}

export async function fetchWebhookDeliveries(
  endpointId: string,
  since?: string,
  axiosInstance?: AxiosInstance
): Promise<WebhookDelivery[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<WebhookDelivery[]>(`/api/webhook-endpoints/${endpointId}/deliveries`, {
    params: since ? { since } : undefined,
  });
  return response.data;
}

/** `POST /api/webhook-endpoints/{id}/redrive` — replay failed deliveries (72h
 * window). Response schema is undeclared on the live spec — treated as opaque. */
export async function redriveWebhookEndpoint(endpointId: string, axiosInstance?: AxiosInstance): Promise<void> {
  const client = axiosInstance || getVoiceFallbackClient();
  await client.post(`/api/webhook-endpoints/${endpointId}/redrive`);
}

/** `POST /api/campaigns/{id}/estimate-burst-cost` — estimate detailed burst capacity and cost split.
 * Backend nests counts/costs under `breakdown` and uses different `rates` key
 * names — normalized here into the flat shape the UI renders. */
export async function estimateBurstCost(
  campaignId: string,
  payload: { projected_sessions: number; avg_duration_minutes: number },
  axiosInstance?: AxiosInstance
): Promise<BurstCostEstimation> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<any>(`/api/campaigns/${campaignId}/estimate-burst-cost`, payload);
  const d = response.data ?? {};
  const b = d.breakdown ?? d;
  const r = d.rates ?? {};
  return {
    normal_sessions: b.normal_sessions ?? 0,
    burst_sessions: b.burst_sessions ?? 0,
    over_capacity_transferred: b.over_capacity_transferred ?? b.over_capacity_rejected ?? 0,
    normal_cost_usd: b.normal_cost_usd ?? 0,
    burst_cost_usd: b.burst_cost_usd ?? 0,
    total_estimated_usd: b.total_estimated_usd ?? 0,
    rates: {
      normal_rate_per_min: r.normal_per_minute_usd ?? r.normal_rate_per_min ?? 0,
      burst_rate_per_min: r.burst_per_minute_usd ?? r.burst_rate_per_min ?? 0,
    },
  };
}

